import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ChevronDown from '../../images/ico_chevron_down.svg'
import { useController, Control } from 'react-hook-form'
interface SelectProps {
  name: string
  options: Options[]
  placeHolderText: string
  dataFromSelect?: (value: Options['value']) => any
  required?: boolean
  control?: Control<any>
  rules?: any
}

export interface Options {
  value: string
  label: string
}
const NativeSelect: React.FC<SelectProps> = ({name, options, placeHolderText, dataFromSelect, required = false, control, rules, ...rest  }) => {
    const [value, setValue] = useState<string>('')
    const [open, setOpen] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState<number>(-1)
    const {fieldState} = useController({name, control, rules})

    const ref = useRef<HTMLDivElement>(null)

    const selectedOption = options.find(o => o.value === value)

    // per chiudere la select se si clicca fuori
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
                setFocusedIndex(-1)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement | HTMLUListElement>) => {
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setOpen(true)
                setFocusedIndex(0)
            }
            return
        }
        // switch per il funzionamento da tastiera
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setFocusedIndex(prev => (prev + 1) % options.length)
                break
            case 'ArrowUp':
                e.preventDefault()
                setFocusedIndex(prev => (prev - 1 + options.length) % options.length)
                break
            case 'Enter':
            case ' ':
                e.preventDefault()
                if (focusedIndex >= 0 && focusedIndex < options.length) {
                    const option = options[focusedIndex]
                    setValue(option.value)
                    dataFromSelect?.(option.value)
                    setOpen(false)
                    setFocusedIndex(-1)
                }
                break
            case 'Escape':
                e.preventDefault()
                setOpen(false)
                setFocusedIndex(-1)
                break
        }
    }

    const itemsRef = useRef<(HTMLLIElement | null)[]>([])

    useEffect(() => {
        if (focusedIndex >= 0 && itemsRef.current[focusedIndex]) {
            itemsRef.current[focusedIndex]?.scrollIntoView({block: 'nearest'})
        }
    }, [focusedIndex])

    return (
        <Wrapper ref={ref}>
            <StyledControl
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-required={required}
                onKeyDown={handleKeyDown}
                onClick={() => {
                    setOpen(o => !o)
                    setFocusedIndex(open ? -1 : 0)
                }}
                {...rest }
            >
                <span>{selectedOption ? selectedOption.label : placeHolderText}</span>
                <Chevron $open={open}/>
            </StyledControl>

            {open && (
                <Menu role="listbox" tabIndex={-1} onKeyDown={handleKeyDown}>
                    {options.map((o, id) => (
                        <Item
                            key={o.value}
                            role="option"
                            aria-selected={o.value === value}
                            $selected={o.value === value || id === focusedIndex}
                            ref={e => (itemsRef.current[id] = e)}
                            onClick={() => {
                                setValue(o.value)
                                dataFromSelect?.(o.value)
                                setOpen(false)
                                setFocusedIndex(-1)
                            }}
                        >
                            {o.label}
                        </Item>
                    ))}
                </Menu>
            )}
            {fieldState.error && (
                <ErrorText>{fieldState.error.message}</ErrorText>
            )}
        </Wrapper>
    )
}

export default NativeSelect


const Wrapper = styled.div`
    position: relative;
    width: 100%;
`

const Select = styled.select`
    background-color: #e8e8e8;
    width: 100%;
    border: solid 1px #fff;
    color: #606060;
    font-weight: 700;
    font-family: TradeGothicLTPro, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
    display: block;
    -webkit-appearance: none;
    line-height: 1;
    font-size: 1.3rem;
    border-radius: 2.3rem;
    min-height: 4.6rem;
    padding: 1rem 1.5rem;
    background-image: url(${ChevronDown});
    background-position: center right 10px;
    background-repeat: no-repeat;

    &:focus {
        outline: none;
    }
`

const StyledControl = styled.button`
    width: 100%;
    min-height: 4.6rem;
    padding: 1rem 3rem 1rem 1.5rem;
    font-size: 1.3rem;
    font-weight: 700;
    font-family: TradeGothicLTPro, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';    
    color: #606060;
    background-color: #e8e8e8;
    border: none;
    border-radius: 2.3rem;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:focus {
       outline: 2px solid #005fcc;
    }
`

const Chevron = styled.span<{ $open: boolean }>`
  width: 16px;
  height: 16px;
  background: url(${ChevronDown}) no-repeat center;
  transition: transform 0.2s ease;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
`

const Menu = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 999;
  margin: 0;
  padding: 0.5rem 0;
  list-style: none;
  background: #ffffff;
  border-radius: 1.5rem;
  box-shadow: 0 3px 18px rgba(45, 46, 46, 0.15);
  max-height: 240px;
  overflow-y: auto;
`

const Item = styled.li<{ $selected: boolean }>`
  padding: 0.8rem 1.5rem;
  font-size: 1.3rem;
  cursor: pointer;
 color: black;
  background-color: ${({ $selected }) =>
    $selected ? '#fcbb00' : 'transparent'};
`

const ErrorText = styled.div`
  color: black;
    font-weight: bold ;
    background-color: #FCBB00;
    border-radius: 0.7rem;
    padding: .4rem;
    line-height: 1.3rem;
    border: 1px solid black;
    font-size: 1.3rem;
    margin-top: .3rem;
`